import { useAssistantState } from '@assistant-ui/react'
import { useContext } from 'react'
import { ThreadVariantContext } from '#app/components/assistant-ui/thread.js'
import {
	RutaActionsContext,
	RutaLinkView,
	RutaView,
	RutorFooterView,
	type RutaAction,
} from '#app/components/rutor/ruta-view.tsx'
import { extractLastDataPart } from '#app/utils/data-parts.js'
import {
	chatLocation,
	codeClassification,
	linkTarget,
	trackClickOut,
	trackCopy,
	trackUIEvent,
} from '#app/utils/plausible.client.js'
import {
	rutaMarkersForCopy,
	type Ruta,
	type RutorPart,
} from '#app/utils/rutor.ts'

/**
 * Lim mellan assistentens chatt (assistant-ui) och den generiska renderaren
 * (components/rutor/ruta-view.tsx): läser meddelandets sista `rutor`-post.
 *
 * - <RutaBox>/<RutaLink>: rutor som modellen infogat med `[[ruta:id]]`
 *   (remark-rutor.ts), som block respektive länk mitt i en mening.
 * - <RutorFooter>: raderna under svaret.
 */

function useRutor(): RutorPart | null {
	return useAssistantState(({ message }) =>
		extractLastDataPart<RutorPart>(
			message.metadata as Record<string, unknown> | undefined,
			'rutor',
		),
	)
}

function useRuta(id: string): Ruta | null {
	return useRutor()?.rutor.find(r => r.id === id) ?? null
}

/** Kopiera/exportera: markören blir rutans kopieringstext. */
export function rutorForCopy(
	text: string,
	metadata: Record<string, unknown> | undefined,
): string {
	const part = extractLastDataPart<RutorPart>(metadata, 'rutor')
	return rutaMarkersForCopy(text, part?.rutor ?? [])
}

/** Rutsort för statistiken, ur ikonen (kontraktet har ingen egen sort). */
const RUTA_KIND: Record<string, string> = {
	hash: 'koder',
	book: 'lathund',
	calculator: 'kalkylator',
}

/**
 * Översätter vad användaren gjorde i en ruta till Plausible (kartan överst i
 * plausible.client.ts): öppna = click_out med position ruta_link, kopiera =
 * button_click copy, fäll ut = button_click ruta_expand. Renderaren räknar
 * kopiera/fäll ut en gång per element; länkar räknas per klick.
 */
function RutaTracking({
	ruta,
	children,
}: {
	ruta?: Ruta
	children: React.ReactNode
}) {
	const location = chatLocation(useContext(ThreadVariantContext))
	const onAction = (action: RutaAction) => {
		const icon = (action.type === 'open' && action.icon) || ruta?.icon
		const kind = (icon ? RUTA_KIND[icon] : undefined) ?? 'annan'
		if (action.type === 'expand') {
			trackUIEvent({
				eventName: 'button_click',
				label: 'ruta_expand',
				location,
				extraProps: { what: action.what, ruta: kind },
			})
			return
		}
		const classification = codeClassification(action.rowCode)
		const props = {
			ruta: kind,
			part: action.part,
			...(classification ? { classification } : {}),
		}
		if (action.type === 'copy') {
			trackCopy({ target: 'ruta', location, extraProps: props })
			return
		}
		const link = linkTarget(action.href)
		if (!link) return
		trackClickOut({
			destinationUrl: action.href,
			destinationSource: link.source,
			isInternal: link.isInternal,
			position: 'ruta_link',
			location,
			extraProps: props,
		})
	}
	return (
		<RutaActionsContext.Provider value={{ onAction }}>
			{children}
		</RutaActionsContext.Provider>
	)
}

/** Okänd ruta (fel id, eller datan inte framme än) → ingenting, aldrig råtext. */
export function RutaBox({ id }: { id: string }) {
	const ruta = useRuta(id)
	return ruta ? (
		<RutaTracking ruta={ruta}>
			<RutaView ruta={ruta} />
		</RutaTracking>
	) : null
}

export function RutaLink({ id }: { id: string }) {
	const ruta = useRuta(id)
	return ruta ? (
		<RutaTracking ruta={ruta}>
			<RutaLinkView ruta={ruta} />
		</RutaTracking>
	) : null
}

export function RutorFooter() {
	const part = useRutor()
	return part && part.footer.length > 0 ? (
		<RutaTracking>
			<RutorFooterView footer={part.footer} />
		</RutaTracking>
	) : null
}
